import axios from "../../../../api/axios";
import { User } from "../../../../types/types";

type ChatMember = {
  user_id: number;
  role: string;
  joined_at: string;
  last_read_msg_id: string | null;
  user: User;
};

export interface GetOrCreateRoomResponse {
  data: {
    chatId: number;
    created: boolean;
    members: ChatMember[];
  };
}

export interface GetOrCreateRoomRequest {
  otherUserId: number;
}

export interface AddMessageRequest {
  room: number;
  type: string;
  text: string;
  meta?: string;
  isGino: boolean;
}

export interface AddMessageResponse {
  data: {
    id: number;
    chat_id: number;
    sender_id: number;
    type: string;
    text: string;
    meta: string | null;
    created_at: string;
    edited_at: string | null;
    deleted_at: string | null;
  };
}

export const getOrCreateDirectRoom = async (
  params: GetOrCreateRoomRequest
): Promise<GetOrCreateRoomResponse["data"]> => {
  const response = await axios.post<GetOrCreateRoomResponse>("/chat-ws/direct/get-or-create", params);
  return response.data.data;
};

export interface ChatMessage {
  id: number;
  chat_id: number;
  sender_id: number;
  type: string;
  text: string;
  meta: string | null;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
}

export const sendMessage = async (chatId: number, params: AddMessageRequest): Promise<AddMessageResponse["data"]> => {
  const response = await axios.post<AddMessageResponse>(`/chat-ws/chats/${chatId}/messages`, params);
  return response.data.data;
};

export type ChatRoom = {
  chatId: number;
  type: string;
  title: null;
  created_at: string;
  updated_at: string;
  last_message?: ChatMessage;
  members: User[];
};
// Parse the monotonic client `seq` (if any) out of a message's JSON meta. Legacy
// rows without a seq fall back to NaN so the id tiebreak still applies.
const parseSeq = (meta: string | null): number => {
  if (!meta) return NaN;
  try {
    const s = (JSON.parse(meta) as { seq?: number }).seq;
    return typeof s === "number" ? s : NaN;
  } catch {
    return NaN;
  }
};

export const getMessages = async (chatId: number): Promise<ChatMessage[]> => {
  const { data } = await axios.get<{ data: ChatMessage[] }>(`/chat-ws/chats/${chatId}/messages`);
  // Backend returns newest-first (ORDER BY created_at DESC). Sort deterministically
  // into chronological order (oldest-first) for rendering. When two messages share
  // a created_at millisecond (a narration + its cards persist back-to-back), the
  // monotonic client `seq` keeps order stable; id is the final fallback.
  return [...data.data].sort((a, b) => {
    const byTime = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    if (byTime !== 0) return byTime;
    const seqA = parseSeq(a.meta);
    const seqB = parseSeq(b.meta);
    if (!Number.isNaN(seqA) && !Number.isNaN(seqB) && seqA !== seqB) return seqA - seqB;
    return a.id - b.id;
  });
};

export const getChatList = async (): Promise<ChatRoom[]> => {
  const response = await axios.get<{ data: ChatRoom[] }>("/chat-ws/my/chats?isGino=true");
  // Surface most-recently-active chats first, keyed off the last message timestamp.
  return [...response.data.data].sort((a, b) => {
    const at = new Date(a.last_message?.created_at ?? a.updated_at).getTime();
    const bt = new Date(b.last_message?.created_at ?? b.updated_at).getTime();
    return bt - at;
  });
};
